import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Lock, X, Download } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useSettings } from "../settings/SettingsContext";
import { Card, SectionTitle, Chip, ChipRow, Empty, LinkButton, useMoney, formatDate } from "../mobile/ui";
import { toCsv, saveCsv } from "../utils/csv";
import { MonthlyBudgetCard, PlannedBudgets, monthlyBudgetOf, oneTimeBudgetsOf } from "./Budgets";
import { isFixedExpense, fixedExpenseTest } from "../utils/bills";
import { isSpending } from "../utils/expenseTypes";

// Spending split into two kinds:
//   Fixed:  expenses that repeat - EMIs, SIPs, rent, subscriptions (anything with a
//           frequency, made automatically from a loan or investment, of a recurring
//           type, or the same payee + amount in more than one month)
//   Random: one-time expenses - shopping, eating out, a gift
// Every expense entry counts in the month it is dated, paid or not.
export { isFixedExpense };

const RANGES = [
  { id: 1, label: "1M" },
  { id: 6, label: "6M" },
  { id: 12, label: "12M" },
];
// Stands for "every type" in the open-type state, in place of one type's name.
const ALL_TYPES = "*";
// The full page has two tabs, one per kind of spending.
const TABS = [
  { id: "fixed", label: "Fixed", title: "Fixed expenses", hint: "EMIs, SIPs, rent, subscriptions - anything that repeats" },
  { id: "random", label: "Random", title: "Random expenses", hint: "One-time spends" },
];

// The open tab is remembered on this device only; it's just a convenience.
const TAB_KEY = "budgetsprint-analysis-tab";
const readTab = () => {
  try {
    return localStorage.getItem(TAB_KEY) === "random" ? "random" : "fixed";
  } catch {
    return "fixed";
  }
};
const saveTab = (t) => {
  try {
    localStorage.setItem(TAB_KEY, t);
  } catch {
    // Storage unavailable - the tab just resets next time.
  }
};

const monthsBack = (count, ref = new Date()) => {
  const months = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(ref.getFullYear(), ref.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString("en-US", { month: "short" }) });
  }
  return months;
};

const monthKeyOf = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getFullYear()}-${d.getMonth()}`;
};

const buildMonthly = (bills, count, isFixed) => {
  const months = monthsBack(count);
  const byKey = Object.fromEntries(months.map((m) => [m.key, { month: m.label, fixed: 0, random: 0 }]));
  for (const b of bills) {
    const row = byKey[monthKeyOf(b.dueDate)];
    if (row) row[isFixed(b) ? "fixed" : "random"] += b.amount;
  }
  return months.map((m) => byKey[m.key]);
};

// The current month day by day, from the 1st to its last day.
const buildDaily = (bills, isFixed, ref = new Date()) => {
  const year = ref.getFullYear();
  const month = ref.getMonth();
  const days = new Date(year, month + 1, 0).getDate();
  const rows = Array.from({ length: days }, (_, i) => ({ month: String(i + 1), fixed: 0, random: 0 }));
  for (const b of bills) {
    const d = new Date(`${b.dueDate}T00:00:00`);
    if (d.getFullYear() === year && d.getMonth() === month) rows[d.getDate() - 1][isFixed(b) ? "fixed" : "random"] += b.amount;
  }
  return rows;
};

// Biggest types first, for one kind over the chosen range.
const byType = (bills, fixed, keys, isFixed) => {
  const totals = {};
  for (const b of bills) {
    if (isFixed(b) !== fixed || !keys.has(monthKeyOf(b.dueDate))) continue;
    totals[b.type] = (totals[b.type] || 0) + b.amount;
  }
  return Object.entries(totals).sort((a, b) => b[1] - a[1]);
};

// `compact` (on Home / the Dashboard) is just the two totals - fixed and random
// expense - for the chosen range, starting on this month. The charts and by-type
// breakdowns are on the full Analysis page.
// `budgets` are rows of the budgets table; `budgetApi` (create / update / remove for
// "budgets") lets the full page set the monthly budget (the compact one only shows it).
const ExpenseAnalysis = ({ bills: allBills, budgets = [], budgetApi, compact = false }) => {
  // Money lent isn't spending - you expect it back (it's on "People owe me") - and
  // neither is paying back a loan or a card's bill.
  const bills = allBills.filter(isSpending);
  const isFixed = fixedExpenseTest(bills);
  const { tokens, fonts } = useTheme();
  const { settings } = useSettings();
  const money = useMoney();
  // Starts on this month (1M) everywhere; 6M and 12M are a tap away.
  const [range, setRange] = useState(1);
  const [tab, setTab] = useState(readTab);
  // The type whose expenses are listed under each kind's breakdown ("" = none open).
  const [openType, setOpenType] = useState({ fixed: "", random: "" });
  const toggleType = (kind, type) => setOpenType((o) => ({ ...o, [kind]: o[kind] === type ? "" : type }));

  const colors = { fixed: tokens.chartInflow, random: tokens.chartOutflow };
  // One month is shown day by day from the 1st; longer ranges month by month.
  const daily = range === 1;
  const monthly = daily ? buildDaily(bills, isFixed) : buildMonthly(bills, range, isFixed);
  const keys = new Set(monthsBack(range).map((m) => m.key));
  const totalFixed = monthly.reduce((s, m) => s + m.fixed, 0);
  const totalRandom = monthly.reduce((s, m) => s + m.random, 0);
  const total = totalFixed + totalRandom;
  const thisMonth = buildMonthly(bills, 1, isFixed)[0];
  const monthName = new Date().toLocaleDateString("en-US", { month: "short" });
  const chartTitle = daily ? `Spending by day · ${monthName} 1–${monthly.length}` : "Spending by month";
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

  // Newest first, and only what falls in the chosen range.
  const newestFirst = (a, b) => (a.dueDate < b.dueDate ? 1 : a.dueDate > b.dueDate ? -1 : 0);
  const inRange = bills.filter((b) => keys.has(monthKeyOf(b.dueDate))).sort(newestFirst);
  const rangeSlug = daily ? "this-month" : `last-${range}-months`;
  const exportColumns = [
    ["Date", (b) => b.dueDate],
    ["Payee", (b) => b.payee],
    ["Type", (b) => b.type],
    ["Fixed / Random", (b) => (isFixed(b) ? "Fixed" : "Random")],
    ["Amount", (b) => b.amount],
    ["Status", (b) => b.status],
    ["Description", (b) => b.description || ""],
  ];
  // `name` goes in the file name, e.g. budgetsprint-analysis-last-6-months.csv.
  const exportSheet = (rows, name) => saveCsv(`budgetsprint-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`, toCsv(rows, exportColumns));

  const chooseTab = (t) => {
    setTab(t);
    saveTab(t);
  };

  const tooltip = (
    <Tooltip
      cursor={{ fill: tokens.chartGrid }}
      formatter={(value) => money(value)}
      labelFormatter={(label) => (daily ? `${monthName} ${label}` : label)}
      contentStyle={{ backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, borderRadius: 10, color: tokens.textPrimary, boxShadow: tokens.cardShadow }}
      labelStyle={{ color: tokens.textPrimary, fontWeight: 700 }}
    />
  );
  const axes = (
    <>
      <CartesianGrid vertical={false} stroke={tokens.chartGrid} />
      {/* A day-by-day month has ~30 bars; label roughly every 5th day so they don't overlap. */}
      <XAxis dataKey="month" axisLine={false} tickLine={false} interval={daily ? 4 : 0} tick={{ fill: tokens.textMuted, fontSize: 12 }} />
      <YAxis axisLine={false} tickLine={false} width={48} tick={{ fill: tokens.textMuted, fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
    </>
  );

  // Amounts hidden: the charts would give them away, so show a placeholder instead.
  const chartBox = (height, chart) =>
    settings.maskAmounts ? (
      <div style={{ height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, borderRadius: 12, backgroundColor: tokens.neutralSoft }}>
        <Lock size={20} color={tokens.textMuted} />
        <span style={{ fontSize: 13, color: tokens.textMuted, fontWeight: 600 }}>Amounts hidden</span>
      </div>
    ) : (
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">{chart}</ResponsiveContainer>
      </div>
    );

  const Swatch = ({ color }) => <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, backgroundColor: color, flexShrink: 0 }} />;

  const Stat = ({ label, value, color, note }) => (
    <Card style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: tokens.textMuted }}>
        {color && <Swatch color={color} />}
        {label}
      </div>
      <div style={{ marginTop: 4, fontFamily: fonts.mono, fontSize: 17, fontWeight: 700, color: tokens.textPrimary }}>{value}</div>
      {note && <div style={{ marginTop: 4, fontSize: 11, color: tokens.textMuted }}>{note}</div>}
    </Card>
  );

  // One kind's spending by type. Clicking a type lists its expenses underneath;
  // the charts don't change.
  const TypeList = ({ fixed }) => {
    const kind = fixed ? "fixed" : "random";
    const rows = byType(bills, fixed, keys, isFixed);
    if (rows.length === 0) return <div style={{ fontSize: 12, color: tokens.textMuted, paddingTop: 10 }}>None in this period.</div>;
    const max = rows[0][1] || 1;
    const open = openType[kind];
    const showAll = open === ALL_TYPES;
    // The open type's expenses of this kind in the range (or all of them), newest first.
    const entries = open ? inRange.filter((b) => (showAll || b.type === open) && isFixed(b) === fixed) : [];
    const openLabel = showAll ? `All ${kind}` : open;
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <button
            type="button"
            aria-expanded={showAll}
            onClick={() => toggleType(kind, ALL_TYPES)}
            style={{ padding: "4px 10px", borderRadius: 999, border: `1px solid ${showAll ? "transparent" : tokens.surfaceBorder}`, cursor: "pointer", fontSize: 12, fontWeight: 600, backgroundColor: showAll ? tokens.accent : "transparent", color: showAll ? tokens.accentContrast : tokens.textMuted }}
          >
            Show all
          </button>
        </div>
        {rows.map(([type, amount]) => (
          <div
            key={type}
            role="button"
            tabIndex={0}
            aria-expanded={open === type}
            title={`Show ${type} expenses`}
            onClick={() => toggleType(kind, type)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleType(kind, type)}
            style={{ padding: "6px 8px", margin: "0 -8px", borderRadius: 8, cursor: "pointer", backgroundColor: open === type ? tokens.neutralSoft : "transparent" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: tokens.textPrimary, fontWeight: open === type ? 700 : 400 }}>
              <span>{type}</span>
              <span style={{ fontFamily: fonts.mono, fontWeight: 600 }}>{money(amount)}</span>
            </div>
            <div style={{ height: 5, borderRadius: 3, marginTop: 5, backgroundColor: tokens.neutralSoft, overflow: "hidden" }}>
              <div style={{ width: `${(amount / max) * 100}%`, height: "100%", backgroundColor: colors[kind] }} />
            </div>
          </div>
        ))}

        {entries.length > 0 && (
          <div style={{ marginTop: 12, borderRadius: 10, border: `1px solid ${tokens.surfaceBorder}`, padding: "4px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: tokens.textPrimary }}>
                {openLabel} · {entries.length} {entries.length === 1 ? "expense" : "expenses"}
              </div>
              <button
                type="button"
                aria-label={`Download ${openLabel} as a spreadsheet`}
                title="Download as spreadsheet"
                onClick={() => exportSheet(entries, showAll ? `${kind}-all-${rangeSlug}` : `${kind}-${open}-${rangeSlug}`)}
                style={{ display: "flex", padding: 4, border: "none", borderRadius: 6, cursor: "pointer", backgroundColor: tokens.accentSoft, color: tokens.accent }}
              >
                <Download size={16} />
              </button>
              <button
                type="button"
                aria-label="Close list"
                onClick={() => toggleType(kind, open)}
                style={{ display: "flex", padding: 4, border: "none", borderRadius: 6, cursor: "pointer", backgroundColor: tokens.neutralSoft, color: tokens.textPrimary }}
              >
                <X size={16} />
              </button>
            </div>
            {entries.map((b, i) => (
              <div key={b.id ?? `${b.payee}-${b.dueDate}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: `1px solid ${tokens.surfaceBorder}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.payee}</div>
                  <div style={{ fontSize: 11.5, color: tokens.textMuted }}>
                    {formatDate(b.dueDate)}
                    {showAll ? ` · ${b.type}` : ""}
                    {b.status === "Pending" ? " · unpaid" : ""}
                  </div>
                </div>
                <div style={{ fontFamily: fonts.mono, fontSize: 13, fontWeight: 700, color: tokens.textPrimary, whiteSpace: "nowrap" }}>{money(b.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const SeparateCard = ({ kind, title, hint }) => {
    const fixed = kind === "fixed";
    return (
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: fonts.display, fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>
              <Swatch color={colors[kind]} /> {title}
            </div>
            <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>{hint}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>{money(fixed ? totalFixed : totalRandom)}</div>
            <div style={{ fontSize: 11, color: tokens.textMuted }}>{pct(fixed ? totalFixed : totalRandom)}% of spending</div>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          {chartBox(
            180,
            <BarChart data={monthly} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
              {axes}
              {tooltip}
              <Bar dataKey={kind} name={title} fill={colors[kind]} radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          )}
        </div>
        <TypeList fixed={fixed} />
      </Card>
    );
  };

  if (compact) {
    const rangeNote = range === 1 ? "this month" : `last ${range} months`;
    return (
      <div>
        <ChipRow>
          {RANGES.map((r) => <Chip key={r.id} active={range === r.id} onClick={() => setRange(r.id)}>{r.label}</Chip>)}
        </ChipRow>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
          <Stat label="Fixed Expense" color={colors.fixed} value={money(totalFixed)} note={total > 0 ? `${rangeNote} · ${pct(totalFixed)}%` : rangeNote} />
          <Stat label="Random Expense" color={colors.random} value={money(totalRandom)} note={total > 0 ? `${rangeNote} · ${pct(totalRandom)}%` : rangeNote} />
        </div>
        {/* The budget is set on the full Analysis page; here it's only shown. */}
        {monthlyBudgetOf(budgets) && (
          <div style={{ marginTop: 10 }}>
            <MonthlyBudgetCard bills={bills} budgets={budgets} />
          </div>
        )}
      </div>
    );
  }

  // The open tab's kind, and what it covers in the chosen range.
  const current = TABS.find((t) => t.id === tab);
  const isFixedTab = tab === "fixed";
  const tabEntries = inRange.filter((b) => isFixed(b) === isFixedTab);
  const tabTotal = isFixedTab ? totalFixed : totalRandom;

  return (
    <div>
      {/* Two tabs, each with this month's total for that kind. */}
      <div role="tablist" style={{ display: "flex", gap: 6, padding: 4, borderRadius: 14, backgroundColor: tokens.neutralSoft }}>
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              onClick={() => chooseTab(t.id)}
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                backgroundColor: active ? tokens.surface : "transparent",
                boxShadow: active ? tokens.cardShadow : "none",
                color: tokens.textPrimary,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: active ? tokens.textPrimary : tokens.textMuted }}>
                <Swatch color={colors[t.id]} /> {t.label}
              </span>
              <span style={{ display: "block", marginTop: 3, fontFamily: fonts.mono, fontSize: 16, fontWeight: 700 }}>{money(thisMonth[t.id])}</span>
              <span style={{ display: "block", fontSize: 11, color: tokens.textMuted }}>this month</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 10 }}>
        <ChipRow>
          {RANGES.map((r) => <Chip key={r.id} active={range === r.id} onClick={() => setRange(r.id)}>{r.label}</Chip>)}
        </ChipRow>
        {tabEntries.length > 0 && (
          <LinkButton icon={Download} onClick={() => exportSheet(tabEntries, `analysis-${tab}-${rangeSlug}`)}>
            Export
          </LinkButton>
        )}
      </div>

      {/* The monthly budget is for random spending, so it sits on that tab. */}
      {!isFixedTab && (
        <div style={{ marginTop: 10 }}>
          <MonthlyBudgetCard bills={bills} budgets={budgets} api={budgetApi} />
        </div>
      )}

      <SectionTitle>{chartTitle}</SectionTitle>
      {tabTotal === 0 ? (
        <Empty>No {current.label.toLowerCase()} expenses in this period yet.</Empty>
      ) : (
        <SeparateCard kind={tab} title={current.title} hint={current.hint} />
      )}

      {/* Trips, gadgets…: added and filled on the Finances page, shown here too. */}
      {oneTimeBudgetsOf(budgets).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <PlannedBudgets budgets={budgets} />
        </div>
      )}
    </div>
  );
};

export default ExpenseAnalysis;
